import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Linkedin, Facebook, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

type NewsItem = {
  date: string; // ISO
  source: 'linkedin' | 'facebook';
  url: string;
  title: { el: string; en: string };
  excerpt: { el: string; en: string };
  image?: string;
};

// Update this list with your latest 3 social media posts.
const newsItems: NewsItem[] = [
  {
    date: '2025-04-23',
    source: 'linkedin',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7435686987371814912/?actorCompanyId=2803903',
    title: {
      el: 'Η TFT στο συνέδριο EMEA Nutrition & Health Channels της SPX FLOW, Inc.',
      en: 'TFT at the SPX FLOW, Inc. EMEA Nutrition & Health Channels conference',
    },
    excerpt: {
      el: 'Οι ισχυρές συνεργασίες οδηγούν την καινοτομία στην επεξεργασία Τροφίμων!',
      en: 'Strong partnerships drive innovation in Food processing!',
    },
  },
  {
    date: '2025-10-13',
    source: 'linkedin',
    url: 'https://www.linkedin.com/posts/tesco-engineering-s-a-_tft-thesgala-trepko-activity-7422259296714768384-Hv_r',
    title: {
      el: 'Επίσκεψη της TFT μαζί με τη ThesGala και την The Trepko Group στην AD IMLEK',
      en: 'TFT visit with ThesGala and The Trepko Group to AD IMLEK',
    },
    excerpt: {
      el: 'Η ομάδα της TFT, μαζί με την ομάδα της ThesGala και την The Trepko Group, επισκέφθηκαν την AD IMLEK στο Βελιγράδι.',
      en: 'The TFT team, together with the ThesGala team and The Trepko Group, visited AD IMLEK in Belgrade.',
    },
  },
  {
    date: '2025-09-09',
    source: 'linkedin',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7392558674956861440',
    title: {
      el: 'Οι λύσεις της TFT για τον Ποιοτικό Έλεγχο Γάλακτος και Γαλακτοκομικών προϊόντων στη Footech 2025',
      en: 'TFT solutions for Quality Control of Milk and Dairy products at Footech 2025',
    },
    excerpt: {
      el: 'Ο εργαστηριακός εξοπλισμός στηρίζει την Ασφάλεια Τροφίμων και ενισχύει την καινοτομία στη Βιομηχανία.',
      en: 'Laboratory equipment supports Food Safety and drives innovation in the Industry.',
    },
  },
];

const formatDate = (iso: string, language: 'el' | 'en') => {
  const d = new Date(iso);
  return d.toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

export const NewsCarousel = () => {
  const { language, t } = useLanguage();
  const [index, setIndex] = useState(0);

  const total = newsItems.length;
  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);
  const current = newsItems[index];
  const SourceIcon = current.source === 'linkedin' ? Linkedin : Facebook;

  return (
    <section className="py-16 bg-background border-t">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            {t('Τα Νέα μας', 'Our News')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {t(
              'Πρόσφατες ενημερώσεις από τα μέσα κοινωνικής δικτύωσης',
              'Recent updates from our social media'
            )}
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <SourceIcon className="h-4 w-4 text-primary" />
                  <span className="capitalize">{current.source}</span>
                  <span>•</span>
                  <time dateTime={current.date}>{formatDate(current.date, language)}</time>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={prev}
                    aria-label={t('Προηγούμενο', 'Previous')}
                    className="h-8 w-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums px-1">
                    {index + 1}/{total}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={next}
                    aria-label={t('Επόμενο', 'Next')}
                    className="h-8 w-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <h3 className="text-lg md:text-xl font-semibold mb-2">
                {current.title[language]}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                {current.excerpt[language]}
              </p>

              <a
                href={current.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                {t('Δείτε την ανάρτηση', 'View post')}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </CardContent>
          </Card>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {newsItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`${t('Ανάρτηση', 'Post')} ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsCarousel;
